import React, { useCallback, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import { Connexion } from '../modules/Connexion/Connexion.component';
import { Collectivite } from '../modules/Collectivite/Collectivite.component';
import { Actualite } from '../modules/Actualite/Actualite.component';
import { Layout } from '../components/Layout/Layout.component';
import { Accueil } from '../modules/Accueil/Accueil.component';
import { Liens } from '../modules/Liens/Liens.component';

import { Loader } from '../components/Loader/Loader.component';

//import { Password } from '../modules/Password/Password.component';
import { DemandeAbsence } from '../modules/DemandeAbsence/DemandeAbsence.component';
import { NouvelleDemande } from '../modules/NouvelleDemande/NouvelleDemande.component';
import { Autres } from '../modules/Autres/Autres.component';
import { Calendrier } from '../modules/Calendrier/Calendrier.component';
import { FicheSalaire } from '../modules/FicheSalaire/FicheSalaire.component';

import { Droits } from '../modules/Droits/Droits.component';
import { Profil } from '../modules/Profil/Profil.component';

import { loadStoredData, useSessionStore } from '../lib/Store/sessionStore.component';
import { setWebRouterNavigate } from '../lib/Navigation/Navigation.component';
import SelectionFicheAgent from '../modules/SelectionFicheAgent/SelectionFicheAgent.component';
import EspaceValideur from '../modules/EspaceValideur/EspaceValideur.component';
import Planning from '../modules/Planning/Planning.component';
import GestionCET from '../modules/GestionCET/GestionCET.component';
import { Organigramme } from '../modules/Organigramme/Organigramme.component';
import { ConfrontationPlanning } from '../modules/ConfrontationPlanning/ConfrontationPlanning.component';
import Carriere from '../modules/Carriere/Carriere.component';

// Fonction utilitaire pour wrapper les composants avec Layout
// requiresAuth=true : affiche le composant uniquement si authentifié (pages protégées)
// requiresAuth=false : affiche toujours le composant (pages publiques comme la connexion)
const withLayout = (Component: React.ComponentType<any>, showMenu = true, requiresAuth = true) => {
  return () => {
    const { session } = useSessionStore();
    return (
      <Layout showMenu={showMenu}>
        {(!requiresAuth || session?.access_token) ? (<Component />) : null}
      </Layout>
    );
  };
};

// Composant wrapper qui utilise les hooks React Router
const WebSessionWrapper: React.FC<{ Component: React.ComponentType<any> }> = ({ Component }) => {
  const {currentLogin} = useSessionStore.getState().getCurrentInfos();
  const navigate = useNavigate();
  const location = useLocation();

  const webNavigation = {
    navigate: (screen: string, params?: any) => {
      if (params) {
        navigate(`/${params.screen || screen}`, { state: params, replace: true });
      } else {
        navigate(`/${screen}`, { replace: true });
      }
    },
    setParams: (params: any) => {
      navigate(location.pathname, { state: { ...location.state, ...params } });
    }
  };

  const webRoute = {
    params: location.state || undefined
  };

  const sessionProps = {
    identifiantAgent: currentLogin?.identifiant || 0,
    identifiantEntite: currentLogin?.identifiantEntiteDefautEsag || 0,
    identifiantDomaine: currentLogin?.identifiantDomaine || 0,
    identifiantValidation: currentLogin?.identifiantValidation || 0,
  };

  return (
    <Layout showMenu={true}>
      <Component
        navigation={webNavigation}
        session={sessionProps}
        route={webRoute}
      />
    </Layout>
  );
};

// Wrapper générique pour les composants qui ont besoin de la session (version web)
const createWebSessionWrapper = (Component: React.ComponentType<any>) => {
  return () => <WebSessionWrapper Component={Component} />;
};

// Wrappers spécifiques pour les composants
const NouvelleDemandePage = createWebSessionWrapper(NouvelleDemande);
const DemandeAbsencePage = createWebSessionWrapper(DemandeAbsence);
const CalendrierPage = createWebSessionWrapper(Calendrier);
const AccueilPage = createWebSessionWrapper(Accueil);
const EspaceValideurPage = createWebSessionWrapper(EspaceValideur);
const GestionCETPage = createWebSessionWrapper(GestionCET);

// Fonction pour créer une page web paramétrable
export const createWebNouvelleDemandePage = (Component: React.ComponentType<any>) => {
  return createWebSessionWrapper(Component);
};

// Composant pour enregistrer la fonction navigate de react-router
const NavigationSetter = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  useEffect(() => {
    setWebRouterNavigate(navigate);
    console.log('[NavigationSetter] react-router navigate registered');
  }, [navigate]);

  return <>{children}</>;
};

const Navigation = () => {
  const { isLoading, loadingMessage } = useSessionStore();
  const loadSession = useCallback(async() => await loadStoredData(), []);
  useEffect(() => {
    loadSession();
  }, [loadSession]);

  return (
    <View style={styles.navigationContainer}>
    <BrowserRouter>
      <NavigationSetter>
        <Routes>
          <Route path="/" element={withLayout(Connexion, false, false)()} />
          <Route path="/Accueil" element={<AccueilPage />} />
          <Route path="/Liens" element={withLayout(Liens)()} />
          <Route path="/Actualite" element={withLayout(Actualite)()} />
          <Route path="/Collectivite" element={withLayout(Collectivite, false, false)()} />
          <Route path="/DemandeAbsence" element={<DemandeAbsencePage />} />
          <Route path="/NouvelleDemande" element={<NouvelleDemandePage />} />
          <Route path="/Calendrier" element={<CalendrierPage />} />
          <Route path="/FicheSalaire" element={withLayout(FicheSalaire)()} />
          <Route path="/Droits" element={withLayout(Droits)()} />
          <Route path="/Profil" element={withLayout(Profil)()} />
          <Route path="/Autres" element={withLayout(Autres)()} />
          <Route path='/SelectionFicheAgent' element={withLayout(SelectionFicheAgent, false)()} />
          {process.env.NODE_ENV === 'development' && (<Route path='/Planning' element={withLayout(Planning)()} />)}
          {process.env.NODE_ENV === 'development' && (<Route path='/EspaceValideur' element={<EspaceValideurPage/>} />)}
          {process.env.NODE_ENV === 'development' && (<Route path='/GestionCET' element={<GestionCETPage/>} />)}
          {process.env.NODE_ENV === 'development' && (<Route path='/Organigramme' element={withLayout(Organigramme)()} />)}
          {process.env.NODE_ENV === 'development' && (<Route path='/ConfrontationPlanning' element={withLayout(ConfrontationPlanning)()} />)}
          {process.env.NODE_ENV === 'development' && (<Route path='/Carriere' element={withLayout(Carriere)()} />)}
        </Routes>
      </NavigationSetter>
    </BrowserRouter>
    <Loader visible={isLoading} message={loadingMessage} />
    </View>
  );
}

const styles = StyleSheet.create({
  navigationContainer: {
    flex: 1,
    position: 'relative' as any,
  },
});

export default Navigation;
